import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useSearchParams, useLocation } from 'react-router-dom';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Clock, 
  Send,
  CheckCircle,
  Building2,
  Globe
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactUs = () => {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const formRef = useRef(null);
  
  // Get product info from URL parameters
  const productFromUrl = searchParams.get('product');
  const categoryFromUrl = searchParams.get('category');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pre-fill form when product is passed via URL
  useEffect(() => {
    if (productFromUrl) {
      const subject = `Inquiry about ${productFromUrl}`;
      const message = `Hi,\n\nI am interested in learning more about your "${productFromUrl}" product${categoryFromUrl ? ` from the ${categoryFromUrl} category` : ''}.\n\nPlease provide more details about:\n- Minimum order quantity\n- Pricing\n- Customization options\n- Lead time\n\nLooking forward to your response.\n\nThank you!`;
      
      setFormData(prev => ({
        ...prev,
        subject,
        message
      }));
    }
  }, [productFromUrl, categoryFromUrl]);

  // Scroll to form if hash is present
  useEffect(() => {
    if (location.hash === '#inquiry-form' && formRef.current) {
      setTimeout(() => {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [location.hash]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      toast({
        title: "Message Sent!",
        description: "Thank you for contacting us. We'll get back to you within 24 hours.",
      });
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send message. Please try again or email us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-200 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <a href="/" className="text-2xl font-bold text-gray-900 hover:text-blue-600 transition-colors">
                VV CosmoTech
              </a>
              <p className="text-xs text-gray-600">A Division of Vijan Ventures LLP</p>
            </div>
            <div className="flex gap-4">
              <Button 
                onClick={() => window.location.href = '/'}
                variant="outline"
              >
                Back to Home
              </Button>
              <Button 
                onClick={() => window.location.href = '/#contact-form'}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Request Quote
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50">
        <div className="container mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">Get In Touch</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            We'd love to hear from you. Reach out to us for any inquiries about our manufacturing services.
          </p>
        </div>
      </section>

      {/* Contact Information Cards */}
      <section className="py-12 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {/* Phone */}
            <Card className="border-gray-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Phone</h3>
                <a href="tel:+919653226520" className="text-blue-600 hover:underline font-medium">
                  +91 9653226520
                </a>
              </CardContent>
            </Card>

            {/* Email - Info */}
            <Card className="border-gray-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-7 h-7 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">General Inquiries</h3>
                <a href="mailto:info@vvcosmotech.com" className="text-blue-600 hover:underline text-sm">
                  info@vvcosmotech.com
                </a>
              </CardContent>
            </Card>

            {/* Email - Sales */}
            <Card className="border-gray-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-7 h-7 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Sales Team</h3>
                <a href="mailto:sales@vvcosmotech.com" className="text-blue-600 hover:underline text-sm">
                  sales@vvcosmotech.com
                </a>
              </CardContent>
            </Card>

            {/* Office Hours */}
            <Card className="border-gray-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-7 h-7 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Office Hours</h3>
                <p className="text-gray-600 text-sm">Monday - Saturday</p>
                <p className="text-blue-600 font-medium">9:30 AM - 7:00 PM</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Main Content - Form & Map */}
      <section id="inquiry-form" ref={formRef} className="py-12 px-4 bg-slate-50">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Contact Form */}
            <div>
              <Card className="border-gray-200 shadow-xl">
                <CardContent className="p-8">
                  {/* Show product inquiry banner if coming from product page */}
                  {productFromUrl && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                      <p className="text-sm text-blue-800">
                        <span className="font-semibold">Product Inquiry:</span> {productFromUrl}
                        {categoryFromUrl && <span className="text-blue-600"> ({categoryFromUrl})</span>}
                      </p>
                      <p className="text-xs text-blue-600 mt-1">Form pre-filled with your product interest. Feel free to add more details below.</p>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Send className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Send Us a Message</h2>
                      <p className="text-gray-600">We'll respond within 24 hours</p>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit}>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label htmlFor="name" className="text-gray-700 font-medium">Your Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="mt-2"
                          placeholder="John Doe"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-gray-700 font-medium">Mobile Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                          className="mt-2"
                          placeholder="+91 9876543210"
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <Label htmlFor="email" className="text-gray-700 font-medium">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="mt-2"
                        placeholder="your@email.com"
                      />
                    </div>

                    <div className="mb-4">
                      <Label htmlFor="subject" className="text-gray-700 font-medium">Subject *</Label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        className="mt-2"
                        placeholder="What is this regarding?"
                      />
                    </div>

                    <div className="mb-6">
                      <Label htmlFor="message" className="text-gray-700 font-medium">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={5}
                        className="mt-2"
                        placeholder="Tell us more about your requirements..."
                      />
                    </div>

                    <Button 
                      type="submit" 
                      size="lg"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-white border-r-transparent mr-2"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <Send className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Location & Map */}
            <div className="space-y-6">
              {/* Office Location Card */}
              <Card className="border-gray-200 shadow-xl">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Visit Our Office</h2>
                      <p className="text-gray-600">Andheri West, Mumbai</p>
                    </div>
                  </div>

                  <div className="space-y-4 mb-6">
                    <div className="flex items-start gap-3">
                      <Building2 className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-900">VV CosmoTech</p>
                        <p className="text-gray-600">Andheri West, Mumbai, Maharashtra, India</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-900">Business Hours</p>
                        <p className="text-gray-600">Monday - Saturday: 9:30 AM - 7:00 PM</p>
                        <p className="text-gray-600">Sunday: Closed</p>
                      </div>
                    </div>
                  </div>

                  <a 
                    href="https://share.google/56tuVuzCBLrGZsNnO"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block w-full"
                  >
                    <Button className="w-full bg-red-600 hover:bg-red-700 text-white">
                      <MapPin className="mr-2 h-5 w-5" />
                      Open in Google Maps
                    </Button>
                  </a>
                </CardContent>
              </Card>

              {/* Map Embed */}
              <Card className="border-gray-200 shadow-xl overflow-hidden">
                <div className="w-full h-96">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3769.8534898457845!2d72.8342647!3d19.1334321!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTnCsDA4JzAwLjQiTiA3MsKwNTAnMDMuNCJF!5e0!3m2!1sen!2sin!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="VV CosmoTech Office Location"
                  ></iframe>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-12 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose VV CosmoTech?</h2>
            <p className="text-xl text-gray-600">Your trusted partner in beauty manufacturing</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Quality Assured</h3>
              <p className="text-gray-600">ISO 9001:2015 & FDA certified manufacturing facility</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Expert Team</h3>
              <p className="text-gray-600">Experienced chemists and formulators at your service</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Fast Turnaround</h3>
              <p className="text-gray-600">Quick delivery without compromising on quality</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4">
        <div className="container mx-auto text-center">
          <p className="text-gray-400 mb-4">
            &copy; {new Date().getFullYear()} VV CosmoTech. All rights reserved.
          </p>
          <div className="flex justify-center gap-6 text-sm">
            <a href="/" className="text-gray-400 hover:text-white transition-colors">Home</a>
            <a href="/contact" className="text-gray-400 hover:text-white transition-colors">Contact</a>
            <a href="/admin/login" className="text-gray-500 hover:text-blue-400 transition-colors">Admin Login</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactUs;
